/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { fetchLaboratories, deleteLab } from '@store/actions/lab.action'

import InputField from '@components/common/InputField/InputField.component'
import LabSharedTable from '@components/pages/Lab/LabSharedTable'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import Button from '@components/common/Button/Button.component'
import AddLaboratory from './AddLaboratory'
import './LaboratoryList.css'
import { useAppDispatch } from '@store/hooks'
import { Laboratory } from '@dataTypes/lab.types'

interface LaboratoryListProps {
  show: boolean
  onClose: () => void
}

const LaboratoryList: React.FC<LaboratoryListProps> = ({ show, onClose }) => {
  const dispatch = useAppDispatch()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showAddLabModal, setShowAddLabModal] = useState<boolean>(false)
  const [editLab, setEditLab] = useState<Laboratory | null>(null)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [noteModal, setNoteModal] = useState<{ open: boolean; text: string }>({
    open: false,
    text: '',
  })

  const labs = useSelector((state: RootState) => state.lab.laboratories.labs)

  useEffect(() => {
    dispatch(fetchLaboratories())
  }, [dispatch])

  const filteredLabs = labs.filter((lab) =>
    lab.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const formatUSPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '')
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone
  }

  const handleViewNotes = (notes: string): void => {
    setNoteModal({ open: true, text: notes })
  }

  const columns = [
    { key: 'name', label: 'Lab name', style: { width: '180px' } },
    {
      key: 'phoneNumber',
      label: 'Phone number',
      style: { width: '150px' },
      formatter: (val: string) => formatUSPhoneNumber(val),
    },
    { key: 'address', label: 'Address', style: { width: '200px' } },
    { key: 'email', label: 'Email', style: { width: '200px' } },
    {
      key: 'isActive',
      label: 'Is active?',
      style: { width: '100px' },
      formatter: (val: boolean) => (
        <span className={`lab-is-active'}`}>{val ? 'Yes' : 'No'}</span>
      ),
    },
    {
      key: 'services',
      label: 'Services offered',
      style: { width: '220px' },
      formatter: (val: any[]) =>
        val.map((s) => (typeof s === 'string' ? s : s.type)).join(', '),
    },
    {
      key: 'notes',
      label: 'Notes',
      style: { width: '150px' },
      formatter: (_: any, row: any) => (
        <Button
          size="sm"
          className="p-0 button-link"
          onClick={() => handleViewNotes(row.notes)}
        >
          View
        </Button>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      style: { width: '120px' },
      formatter: (_: any, row: any) => (
        <div className="d-flex justify-content-center">
          <ActionButtons
            onEdit={() => {
              setEditLab(row)
              setIsEditMode(true)
              setShowAddLabModal(true)
            }}
            onDelete={() => dispatch(deleteLab(row.id))}
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        dialogClassName="modal-xxl"
        centered
        backdrop="static"
      >
        <div style={{ position: 'relative' }}>
          {(showAddLabModal || noteModal.open) && (
            <div className="modal-dark-overlay" />
          )}
          <Modal.Header closeButton>
            <Modal.Title>Laboratory list</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-3">
              <Col className="d-flex justify-content-end align-items-center gap-2">
                <InputField
                  placeholder="Search laboratories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftIcon={<i className="ti ti-search" />}
                  className="w-auto"
                />
                <Button className="button-secondary">Import / Export</Button>
                <Button
                  className="button-primary"
                  onClick={() => {
                    setShowAddLabModal(true)
                    setIsEditMode(false)
                    setEditLab(null)
                  }}
                >
                  Add Lab
                </Button>
              </Col>
            </Row>

            <div className="laboratory-list-table">
              <LabSharedTable
                columns={columns}
                data={filteredLabs}
                pageSize={5}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button className="button-secondary" onClick={onClose}>
              Close
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <AddLaboratory
        show={showAddLabModal}
        onClose={() => {
          setShowAddLabModal(false)
          setEditLab(null)
          setIsEditMode(false)
        }}
        editLab={editLab}
        isEditMode={isEditMode}
      />

      <Modal
        show={noteModal.open}
        onHide={() => setNoteModal({ open: false, text: '' })}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ whiteSpace: 'pre-wrap' }}>{noteModal.text}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="button-secondary"
            onClick={() => setNoteModal({ open: false, text: '' })}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default LaboratoryList
